import { useState } from "react";
import { Check, Copy } from "react-bootstrap-icons";
import "./CopyText.css";

export interface CopyTextProps {
  value: string;
  disabled?: boolean;
  onCopy?: () => void;
}

/**
 * Copy-to-clipboard button component with visual feedback.
 *
 * @param props - The component props
 * @param props.value - Text content to be copied to clipboard
 * @param props.disabled - Optional. When true, disables the copy functionality
 * @param props.onCopy - Optional callback invoked after successful copy
 */
export const CopyText = ({
  value,
  disabled = false,
  onCopy,
}: CopyTextProps): React.ReactElement => {
  const [copied, setCopied] = useState(false);

  /**
   * Copies text to clipboard and shows confirmation feedback.
   */
  const handleCopy = async () => {
    if (disabled) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (onCopy) onCopy();
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.warn("Failed to copy text: ", error);
    }
  };

  return (
    <div
      className={`copy-text-container ${disabled ? "disabled" : ""}`}
      onClick={handleCopy}
      title={copied ? "Copied!" : "Copy to Clipboard"}
    >
      {copied ? <Check className="copied-icon" /> : <Copy className="copy-icon" />}
    </div>
  );
};

CopyText.displayName = "CopyText";
